/**
 * Validation pure du champ "Détail de votre demande" sur l'écran de
 * demande de droits d'accès ( cf {@code AuthorizationsRightsRequestInfoView} ) .
 *
 * <p><b>Pourquoi</b> ( ticket UX ) : le champ est marqué obligatoire
 * ( astérisque rouge ) mais l'envoi d'un commentaire vide ne produisait
 * aucun retour visible côté utilisateur ( return silencieux dans
 * {@code createRequest} ) , donnant l'impression que le bouton "Enregistrer"
 * ne fonctionnait pas . Solution : helper pur + intégration dans le
 * pipeline du formulaire + i18n + tests .
 *
 * <p><b>SOLID</b> :
 * <ul>
 *   <li><b>SRP</b> : ce module fait UNIQUEMENT la validation . L'affichage
 *       ( toast , bordure rouge , texte d'aide ) est porté par les
 *       composants en aval ;</li>
 *   <li><b>OCP</b> : ajouter une nouvelle règle ( ex max-length , regexp
 *       interdite ) = ajouter une clé d'erreur dans {@link CommentErrorKey}
 *       et un test , sans modifier le contrat existant ;</li>
 *   <li><b>DRY</b> : trim + bornes regroupés ici , consommés par
 *       {@code FieldsForm} + view parent + tests sans duplication .</li>
 * </ul>
 */

/** Longueur minimale exigée par défaut pour le commentaire de demande . */
export const DEFAULT_COMMENT_MIN_LENGTH = 5;

/**
 * Identifie la cause d'invalidité . {@code null} = valide . Les valeurs
 * sont aussi des clés i18n directement utilisables côté UI
 * ( {@code rightsRequest.detail.required} , etc. ) .
 */
export type CommentErrorKey =
  | "rightsRequest.detail.required"
  | "rightsRequest.detail.tooShort"
  | null;

export interface CommentValidationResult {
  /** Vrai si le commentaire est valide ( prêt à soumettre ) . */
  valid: boolean;
  /** Clé i18n du message d'erreur , ou {@code null} si valide . */
  errorKey: CommentErrorKey;
  /**
   * Paramètres d'interpolation à passer à i18n.t() avec {@link errorKey} ,
   * pour les messages comportant des placeholders ( ex minLength ) .
   */
  params: Record<string, number>;
}

/**
 * Valide la chaîne saisie par l'utilisateur , en appliquant un trim
 * pour qu'une suite d'espaces ne soit pas considérée valide .
 *
 * @param raw        valeur brute saisie ( peut être null / undefined si
 *                   le composant parent n'a pas encore initialisé son
 *                   ref )
 * @param minLength  longueur minimale exigée , défaut
 *                   {@link DEFAULT_COMMENT_MIN_LENGTH}
 */
export function validateRequestComment(
  raw: string | null | undefined,
  minLength: number = DEFAULT_COMMENT_MIN_LENGTH,
): CommentValidationResult {
  const trimmed = (raw ?? "").trim();
  if (trimmed.length === 0) {
    return {
      valid: false,
      errorKey: "rightsRequest.detail.required",
      params: { minLength },
    };
  }
  if (trimmed.length < minLength) {
    return {
      valid: false,
      errorKey: "rightsRequest.detail.tooShort",
      params: { minLength },
    };
  }
  return { valid: true, errorKey: null, params: {} };
}
